import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  alpha,
  useTheme,
  Avatar,
  AvatarGroup,
  Container,
  Card,
  CardContent,
  Divider,
  Chip,
  Grid,
  Tabs,
  Tab,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton as MuiIconButton,
} from '@mui/material';
import { format, parseISO, isValid } from 'date-fns';
import EventModal, { EventData as ModalEventData, RecurrenceRule } from '../components/EventModal';
import { courtApi, recurringEventApi, facilityApi, eventApi } from '../api';
import { Court, RecurringEvent } from '../api_types';
import { logger } from '../shared/logger';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import RepeatIcon from '@mui/icons-material/Repeat';
import RecurringEventModal from '../components/RecurringEventModal';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import { EventData as ApiEventData } from '../api_types';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { invalidateEventsCache } from './dashboard/UpcomingEventsDashboard';

interface BusinessEventsProps {
  facilityId: string;
  businessId: string;
}

interface BusinessHours {
  [day: string]: {
    isClosed: boolean;
    openTime: string;
    closeTime: string;
  };
}

interface UserData {
  firstName: string;
  lastName: string;
  email?: string;
}

interface ParticipantData {
  userId: string;
  signUpDate: string;
  name?: string;
  userData?: UserData;
}

// Define our own EventData interface that matches the API response and ModalEventData
interface EventData extends Omit<ModalEventData, 'status' | 'id'> {
  id?: string;
  status: 'scheduled' | 'cancelled' | 'completed';
  participants?: ParticipantData[];
  isRecurring?: boolean;
  recurringEventId?: string;
  recurrenceRule?: RecurrenceRule;
  price?: number;
}

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'scheduled':
        return {
          color: '#0a2540',
          backgroundColor: '#cbf4c9',
        };
      case 'cancelled':
        return {
          color: '#cd3d64',
          backgroundColor: '#ffd7d7',
        };
      case 'completed':
        return {
          color: '#4f566b',
          backgroundColor: '#e3e8ee',
        };
      default:
        return {
          color: '#4f566b',
          backgroundColor: '#e3e8ee',
        };
    }
  };

  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
        letterSpacing: '0.025em',
        display: 'inline-flex',
        alignItems: 'center',
        ...getStatusColor(),
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: 'currentColor',
          mr: 1,
          opacity: 0.5,
        }}
      />
      {status}
    </Box>
  );
};

const BusinessEvents: React.FC<BusinessEventsProps> = ({ facilityId, businessId }) => {
  const theme = useTheme();
  const [events, setEvents] = useState<EventData[]>([]);
  const [recurringEvents, setRecurringEvents] = useState<RecurringEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showRecurringEventModal, setShowRecurringEventModal] = useState(false);
  const [courts, setCourts] = useState<Court[]>([]);
  const [selectedRecurringEvent, setSelectedRecurringEvent] = useState<RecurringEvent | undefined>(undefined);
  const [businessHours, setBusinessHours] = useState<BusinessHours>({});
  const [timeOptions, setTimeOptions] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedEventForPlayers, setSelectedEventForPlayers] = useState<EventData | null>(null);
  const [isPlayersModalOpen, setIsPlayersModalOpen] = useState(false);

  const fetchCourts = useCallback(async () => {
    try {
      const response = await courtApi.getCourts(businessId, facilityId);
      setCourts(response);
    } catch (err) {
      logger.error('Failed to fetch courts', { error: err });
      setError('Failed to fetch courts');
    }
  }, [facilityId]);

  const fetchRecurringEvents = useCallback(async () => {
    try {
      const response = await recurringEventApi.getRecurringEvents(businessId, facilityId);
      setRecurringEvents(response);
    } catch (err) {
      logger.error('Failed to fetch recurring events', { error: err });
      setError('Failed to fetch recurring events');
    }
  }, [facilityId]);

  const fetchBusinessHours = useCallback(async () => {
    try {
      console.log('Fetching business hours for:', { businessId, facilityId });
      const hours = await facilityApi.getFacilityBusinessHours(businessId, facilityId);
      console.log('Received business hours:', hours);
      setBusinessHours(hours);
    } catch (err) {
      logger.error('Failed to fetch business hours', { error: err });
      setError('Failed to fetch business hours');
    }
  }, [businessId, facilityId]);

  const generateTimeOptions = useCallback((date: string) => {
    try {
      const parsedDate = parseISO(date);
      const dayOfWeek = format(parsedDate, 'EEEE').toLowerCase();
      const hours = businessHours[dayOfWeek];
      
      if (!hours) {
        console.warn('No business hours found for', dayOfWeek);
        setTimeOptions([]);
        return;
      }

      if (hours.isClosed) {
        setTimeOptions([]);
        return;
      }

      if (!hours.openTime || !hours.closeTime) {
        console.warn('Invalid business hours for', dayOfWeek);
        setTimeOptions([]);
        return;
      }

      const options = [];
      const [openHour, openMinute] = hours.openTime.split(':').map(Number);
      const [closeHour, closeMinute] = hours.closeTime.split(':').map(Number);

      const openTimeMinutes = openHour * 60 + openMinute;
      const closeTimeMinutes = closeHour * 60 + closeMinute;

      for (let minutes = openTimeMinutes; minutes < closeTimeMinutes; minutes += 30) {
        const hour = Math.floor(minutes / 60);
        const minute = minutes % 60;
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push(time);
      }

      setTimeOptions(options);
    } catch (error) {
      console.error('Error generating time options:', error);
      setTimeOptions([]);
    }
  }, [businessHours]);

  const validateEventTime = useCallback((date: string, startTime: string, endTime: string): string | null => {
    const dayOfWeek = format(parseISO(date), 'EEEE').toLowerCase();
    const hours = businessHours[dayOfWeek];
    
    if (!hours) {
      return 'Business hours not available for this day';
    }

    if (hours.isClosed) {
      return 'The facility is closed on this day';
    }

    if (!hours.openTime || !hours.closeTime) {
      return 'Invalid business hours configuration';
    }

    const [eventStartHour, eventStartMinute] = startTime.split(':').map(Number);
    const [eventEndHour, eventEndMinute] = endTime.split(':').map(Number);
    const [openHour, openMinute] = hours.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = hours.closeTime.split(':').map(Number);

    const eventStartMinutes = eventStartHour * 60 + eventStartMinute;
    const eventEndMinutes = eventEndHour * 60 + eventEndMinute;
    const openTimeMinutes = openHour * 60 + openMinute;
    const closeTimeMinutes = closeHour * 60 + closeMinute;

    if (eventStartMinutes < openTimeMinutes) {
      return `Event cannot start before business hours (${hours.openTime})`;
    }

    if (eventEndMinutes > closeTimeMinutes) {
      return `Event cannot end after business hours (${hours.closeTime})`;
    }

    if (eventStartMinutes >= eventEndMinutes) {
      return 'Event end time must be after start time';
    }

    return null;
  }, [businessHours]);

  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        fetchCourts(),
        fetchRecurringEvents(),
        fetchBusinessHours()
      ]);
      
      // Fetch events using the API
      const eventsData = await eventApi.getFacilityEvents(businessId, facilityId);
      setEvents(eventsData as EventData[]);
    } catch (err) {
      setError('Failed to fetch events');
      logger.error('Error fetching events:', { error: err });
    } finally {
      setLoading(false);
    }
  }, [facilityId, businessId, fetchCourts, fetchRecurringEvents, fetchBusinessHours]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const handleSaveEvent = async (eventData: EventData) => {
    if (isSaving) {
      console.log('Save already in progress, ignoring duplicate request');
      return;
    }
    
    try {
      setIsSaving(true);
      setLoading(true);
      
      // Validate event time against business hours
      const timeError = validateEventTime(eventData.date, eventData.startTime, eventData.endTime);
      if (timeError) {
        setStatusMessage({ type: 'error', message: timeError });
        setIsSaving(false);
        setLoading(false);
        return;
      }

      // Format the event data to match the API's expected schema
      const apiEventData: ApiEventData = {
        id: eventData.id || '',
        title: eventData.title,
        description: eventData.description,
        date: eventData.date,
        startTime: eventData.startTime,
        endTime: eventData.endTime,
        courtIds: eventData.courtIds,
        capacity: eventData.capacity,
        status: eventData.status || 'scheduled',
        isBlocked: eventData.isBlocked || false,
        isRecurring: eventData.isRecurring || false,
        recurringEventId: eventData.recurringEventId,
        recurrenceRule: eventData.recurrenceRule,
        price: eventData.price || 0
      };

      let response;
      if (modalMode === 'create') {
        response = await eventApi.createEvent(businessId, facilityId, apiEventData);
        setStatusMessage({ type: 'success', message: 'Event created successfully!' });
      } else if (selectedEvent?.id) {
        response = await eventApi.updateEvent(businessId, facilityId, selectedEvent.id, apiEventData);
        setStatusMessage({ type: 'success', message: 'Event updated successfully!' });
      }

      // Invalidate the events cache when an event is created or updated
      invalidateEventsCache(facilityId);
      
      // Refresh all data
      await refreshData();

      // Close the modal
      handleCloseModal();

      // Clear status message after 3 seconds
      setTimeout(() => {
        setStatusMessage(null);
      }, 3000);

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to save event');
      logger.error('Error saving event:', error);
      setStatusMessage({ type: 'error', message: error.message });
    } finally {
      setIsSaving(false);
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      setLoading(true);
      await eventApi.deleteEvent(businessId, facilityId, eventId);
      // Invalidate the events cache when an event is deleted
      invalidateEventsCache(facilityId);
      setStatusMessage({ type: 'success', message: 'Event deleted successfully!' });
      
      // Refresh all data
      await refreshData();

      // Clear status message after 3 seconds
      setTimeout(() => {
        setStatusMessage(null);
      }, 3000);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete event');
      logger.error('Error deleting event:', error);
      setStatusMessage({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  function getEventEndDateTime(event: EventData) {
    try {
      console.log('Processing event:', event);
      const date = parseISO(event.date);
      if (!isValid(date)) {
        console.log('Invalid date:', event.date);
        return null;
      }

      const [hours, minutes] = event.endTime.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) {
        console.log('Invalid time:', event.endTime);
        return null;
      }

      // Create date in local timezone
      const dateTime = new Date(date);
      dateTime.setHours(hours, minutes, 0, 0);
      
      // Adjust for timezone offset
      const timezoneOffset = dateTime.getTimezoneOffset();
      dateTime.setMinutes(dateTime.getMinutes() + timezoneOffset);
      
      console.log('Generated dateTime:', dateTime);
      return dateTime;
    } catch (error) {
      console.error('Error in getEventEndDateTime:', error);
      return null;
    }
  }

  const now = new Date();
  console.log('Current time:', now);
  
  // Filter blocked events
  const blockedEvents = events
    .filter(event => event.isBlocked)
    .map(event => ({ ...event, endDateTime: getEventEndDateTime(event) }))
    .filter(event => event.endDateTime && event.endDateTime >= now)
    .sort((a, b) => (a.endDateTime?.getTime() || 0) - (b.endDateTime?.getTime() || 0));
  
  console.log('Blocked events:', blockedEvents);
  
  // Filter upcoming events (excluding blocked events)
  const upcomingEvents = events
    .filter(event => !event.isBlocked)
    .map(event => ({ ...event, endDateTime: getEventEndDateTime(event) }))
    .filter(event => event.endDateTime && event.endDateTime >= now)
    .sort((a, b) => (a.endDateTime?.getTime() || 0) - (b.endDateTime?.getTime() || 0));
  
  console.log('Upcoming events:', upcomingEvents);

  // Filter past events (excluding blocked events)
  const pastEvents = events
    .filter(event => !event.isBlocked)
    .map(event => ({ ...event, endDateTime: getEventEndDateTime(event) }))
    .filter(event => event.endDateTime && event.endDateTime < now)
    .sort((a, b) => (b.endDateTime?.getTime() || 0) - (a.endDateTime?.getTime() || 0));

  const handleCreateEvent = () => {
    console.log('Current business hours:', businessHours);
    console.log('Today:', format(new Date(), 'EEEE').toLowerCase());
    
    if (!businessHours || Object.keys(businessHours).length === 0) {
      setStatusMessage({ 
        type: 'error', 
        message: 'Please wait while business hours are loading...' 
      });
      return;
    }

    // Check if we have valid business hours for today
    const today = format(new Date(), 'EEEE').toLowerCase();
    const todayHours = businessHours[today];
    console.log('Today hours:', todayHours);
    
    if (!todayHours || !todayHours.openTime || !todayHours.closeTime) {
      setStatusMessage({ 
        type: 'error', 
        message: 'Business hours are not properly configured for today' 
      });
      return;
    }

    setSelectedEvent(null);
    setModalMode('create');
    setIsModalOpen(true);
    // Generate time options for today's date
    generateTimeOptions(format(new Date(), 'yyyy-MM-dd'));
  };

  const handleCreateRecurringEvent = () => {
    setShowRecurringEventModal(true);
    // Generate time options for today's date
    generateTimeOptions(format(new Date(), 'yyyy-MM-dd'));
  };

  const handleEditEvent = (event: EventData) => {
    setSelectedEvent(event);
    setModalMode('edit');
    setIsModalOpen(true);
    // Generate time options for the event's date
    generateTimeOptions(event.date);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setModalMode('create');
    setIsModalOpen(false);
    setIsSaving(false); // Reset saving state when modal closes
  };

  const handleCloseRecurringEventModal = () => {
    setSelectedRecurringEvent(undefined);
    setShowRecurringEventModal(false);
  };

  const handleSaveRecurringEvent = async () => {
    try {
      await fetchRecurringEvents();
      handleCloseRecurringEventModal();
    } catch (error) {
      console.error('Error saving recurring event:', error);
    }
  };

  const handleDeleteRecurringEvent = async (eventId: string) => {
    if (!window.confirm('Are you sure you want to delete this recurring event?')) {
      return;
    }

    try {
      await recurringEventApi.deleteRecurringEvent(businessId, facilityId, eventId);
      // Invalidate the events cache when a recurring event is deleted
      invalidateEventsCache(facilityId);
      setRecurringEvents(prev => prev.filter(event => event.id !== eventId));
    } catch (error) {
      console.error('Error deleting recurring event:', error);
      alert('Failed to delete recurring event. Please try again.');
    }
  };

  const handleEditRecurringEvent = (event: RecurringEvent) => {
    setSelectedRecurringEvent(event);
    setShowRecurringEventModal(true);
    // Generate time options for the event's start date
    generateTimeOptions(event.startDate);
  };

  const handleCardClick = (event: EventData, e: React.MouseEvent) => {
    // Prevent opening modal if clicking edit/delete icons
    const target = e.target as HTMLElement;
    if (
      target.closest('.event-edit-btn') ||
      target.closest('.event-delete-btn')
    ) {
      return;
    }
    setSelectedEventForPlayers(event);
    setIsPlayersModalOpen(true);
  };

  const handleClosePlayersModal = () => {
    setSelectedEventForPlayers(null);
    setIsPlayersModalOpen(false);
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        backgroundColor: '#f6f8fa',
      }}
    >
      {statusMessage && (
        <Alert 
          severity={statusMessage.type}
          sx={{
            position: 'fixed',
            top: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: 300,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            '& .MuiAlert-icon': {
              color: statusMessage.type === 'success' ? '#2e7d32' : '#d32f2f',
            },
            '& .MuiAlert-message': {
              color: statusMessage.type === 'success' ? '#2e7d32' : '#d32f2f',
              fontWeight: 500,
            },
          }}
        >
          {statusMessage.message}
        </Alert>
      )}
      
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b flex items-center justify-between px-8 py-5 mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Events</h1>
        <div className="flex gap-2">
          <button
            onClick={handleCreateRecurringEvent}
            className="inline-flex items-center px-5 py-2.5 border border-indigo-600 text-indigo-600 font-semibold rounded-md shadow-sm hover:bg-indigo-50 transition"
          >
            Create recurring event
          </button>
          <button
            onClick={handleCreateEvent}
            className="inline-flex items-center px-5 py-2.5 bg-indigo-600 text-white font-semibold rounded-md shadow-sm hover:bg-indigo-700 transition"
          >
            Create event
          </button>
        </div>
      </div>

      <Container maxWidth="lg" sx={{ pb: 8 }}>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
              <Tabs 
                value={activeTab} 
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    minWidth: 120,
                  },
                }}
              >
                <Tab label="Recurring Events" />
                <Tab label="Upcoming Events" />
                <Tab label="Blocked Dates" />
                <Tab label="Past Events" />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Box sx={{ mb: 6 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#1a1f36',
                    mb: 3,
                  }}
                >
                  Recurring Events
                </Typography>
                {recurringEvents.length > 0 ? (
                  <Stack spacing={2}>
                    {recurringEvents.map(event => (
                      <Card
                        key={event.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'rgba(0, 0, 0, 0.1)',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#625afa',
                          },
                          transition: 'border-color 0.2s ease',
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: '#1a1f36',
                                  }}
                                >
                                  {event.title}
                                </Typography>
                                <Chip
                                  icon={<RepeatIcon sx={{ fontSize: '1rem' }} />}
                                  label={event.recurrenceRule.frequency}
                                  size="small"
                                  sx={{
                                    backgroundColor: alpha('#625afa', 0.1),
                                    color: '#625afa',
                                    borderRadius: '6px',
                                    height: '24px',
                                    fontSize: '12px',
                                    '& .MuiChip-icon': {
                                      color: '#625afa',
                                    },
                                  }}
                                />
                                {event.recurrenceRule.frequency === 'weekly' && event.recurrenceRule.daysOfWeek && (
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {event.recurrenceRule.daysOfWeek.map(day => {
                                      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                                      return (
                                        <Chip
                                          key={day}
                                          label={days[day]}
                                          size="small"
                                          sx={{
                                            backgroundColor: alpha('#625afa', 0.1),
                                            color: '#625afa',
                                            borderRadius: '6px',
                                            height: '24px',
                                            fontSize: '12px',
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                )}
                              </Box>
                              <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: '#4f566b' }}>
                                  <CalendarTodayOutlinedIcon sx={{ fontSize: '1rem', mr: 1 }} />
                                  <Typography variant="body2">
                                    {format(parseISO(event.startDate), 'MMM d, yyyy')}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: '#4f566b' }}>
                                  <AccessTimeOutlinedIcon sx={{ fontSize: '1rem', mr: 1 }} />
                                  <Typography variant="body2">
                                    {event.startTime} - {event.endTime}
                                  </Typography>
                                </Box>
                              </Stack>
                              {event.description && (
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#4f566b',
                                    fontSize: '14px',
                                    mb: 2,
                                  }}
                                >
                                  {event.description}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {event.courtIds.map(courtId => {
                                  const court = courts.find(c => c.id === courtId);
                                  return court ? (
                                    <Chip
                                      key={courtId}
                                      label={court.name}
                                      size="small"
                                      sx={{
                                        backgroundColor: alpha('#625afa', 0.1),
                                        color: '#625afa',
                                        borderRadius: '6px',
                                        height: '24px',
                                        fontSize: '12px',
                                      }}
                                    />
                                  ) : null;
                                })}
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                onClick={() => handleEditRecurringEvent(event)}
                                sx={{
                                  color: '#4f566b',
                                  '&:hover': {
                                    backgroundColor: alpha('#625afa', 0.04),
                                    color: '#625afa',
                                  },
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDeleteRecurringEvent(event.id)}
                                sx={{
                                  color: '#4f566b',
                                  '&:hover': {
                                    backgroundColor: alpha('#ff4d4f', 0.04),
                                    color: '#ff4d4f',
                                  },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box 
                    sx={{ 
                      py: 6,
                      textAlign: 'center',
                      backgroundColor: '#fff',
                      borderRadius: 1,
                      border: '1px dashed',
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#4f566b',
                        fontSize: '14px',
                      }}
                    >
                      No recurring events scheduled
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ mb: 6 }}>
                <Typography 
                  variant="h6" 
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#1a1f36',
                    mb: 3,
                  }}
                >
                  Upcoming Events
                </Typography>
                {upcomingEvents.length > 0 ? (
                  <Stack spacing={2}>
                    {upcomingEvents.map(event => (
                      <Card
                        key={event.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'rgba(0, 0, 0, 0.1)',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#625afa',
                          },
                          transition: 'border-color 0.2s ease',
                          cursor: 'pointer',
                        }}
                        onClick={(e) => handleCardClick(event, e)}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#1a1f36',
                                  mb: 1,
                                }}
                              >
                                {event.title}
                              </Typography>
                              {/* Courts as mini icons */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {event.courtIds.map(courtId => {
                                  const court = courts.find(c => c.id === courtId);
                                  return court ? (
                                    <Chip
                                      key={courtId}
                                      label={court.name}
                                      size="small"
                                      sx={{
                                        backgroundColor: alpha('#625afa', 0.1),
                                        color: '#625afa',
                                        borderRadius: '6px',
                                        height: '24px',
                                        fontSize: '12px',
                                      }}
                                    />
                                  ) : null;
                                })}
                              </Box>
                              <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: '#4f566b' }}>
                                  <CalendarTodayOutlinedIcon sx={{ fontSize: '1rem', mr: 1 }} />
                                  <Typography variant="body2">
                                    {format(parseISO(event.date), 'MMM d, yyyy')}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: '#4f566b' }}>
                                  <AccessTimeOutlinedIcon sx={{ fontSize: '1rem', mr: 1 }} />
                                  <Typography variant="body2">
                                    {event.startTime} - {event.endTime}
                                  </Typography>
                                </Box>
                                <StatusChip status={event.status} />
                                {event.price !== undefined && (
                                  <Chip
                                    icon={<CreditCardIcon sx={{ color: '#635bff', fontSize: 18, ml: 0.5 }} />}
                                    label={
                                      <span style={{ fontWeight: 600, letterSpacing: 0.2 }}>
                                        {event.price > 0 ? `$${event.price.toFixed(2)}` : 'Free'}
                                      </span>
                                    }
                                    sx={{
                                      background: 'linear-gradient(90deg, #f5f6fa 0%, #e9eafc 100%)',
                                      color: '#1a1f36',
                                      borderRadius: '999px',
                                      boxShadow: '0 1px 4px 0 rgba(99,91,255,0.08)',
                                      fontSize: '15px',
                                      height: '32px',
                                      ml: 2,
                                      pr: 2,
                                      pl: 0.5,
                                      '& .MuiChip-icon': {
                                        marginLeft: '6px',
                                        marginRight: '-2px',
                                      },
                                    }}
                                  />
                                )}
                              </Stack>
                              {event.description && (
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#4f566b',
                                    fontSize: '14px',
                                    mb: 2,
                                  }}
                                >
                                  {event.description}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <MuiIconButton
                                className="event-edit-btn"
                                onClick={(e) => { e.stopPropagation(); handleEditEvent(event); }}
                                sx={{
                                  color: '#4f566b',
                                  '&:hover': {
                                    backgroundColor: alpha('#625afa', 0.04),
                                    color: '#625afa',
                                  },
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </MuiIconButton>
                              <MuiIconButton
                                className="event-delete-btn"
                                onClick={(e) => { e.stopPropagation(); event.id && handleDeleteEvent(event.id); }}
                                sx={{
                                  color: '#4f566b',
                                  '&:hover': {
                                    backgroundColor: alpha('#ff4d4f', 0.04),
                                    color: '#ff4d4f',
                                  },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </MuiIconButton>
                            </Box>
                          </Box>
                          {/* Participants as avatars with full names in tooltips */}
                          {event.participants && event.participants.length > 0 && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Box>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#4f566b',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    mb: 1,
                                  }}
                                >
                                  PARTICIPANTS
                                </Typography>
                                <AvatarGroup max={5} sx={{
                                  '& .MuiAvatar-root': {
                                    width: 28,
                                    height: 28,
                                    fontSize: '13px',
                                    backgroundColor: '#625afa',
                                    border: '2px solid #fff',
                                  }
                                }}>
                                  {event.participants.map((participant) => (
                                    <Tooltip key={participant.userId} title={participant.name || participant.userId}>
                                      <Avatar sx={{ bgcolor: '#625afa' }}>
                                        {participant.name
                                          ? participant.name.split(' ').map(n => n[0]).join('').toUpperCase()
                                          : participant.userId.slice(0, 2).toUpperCase()}
                                      </Avatar>
                                    </Tooltip>
                                  ))}
                                </AvatarGroup>
                              </Box>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box 
                    sx={{ 
                      py: 6,
                      textAlign: 'center',
                      backgroundColor: '#fff',
                      borderRadius: 1,
                      border: '1px dashed',
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#4f566b',
                        fontSize: '14px',
                      }}
                    >
                      No upcoming events scheduled
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 2 && (
              <Box sx={{ mb: 6 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#1a1f36',
                    mb: 3,
                  }}
                >
                  Blocked Time
                </Typography>
                {blockedEvents.length > 0 ? (
                  <Grid container spacing={2}>
                    {blockedEvents.map(event => (
                      <Grid item xs={12} sm={6} md={4} key={event.id}>
                        <Card
                          sx={{
                            border: '1px solid',
                            borderColor: 'rgba(0, 0, 0, 0.1)',
                            boxShadow: 'none',
                            '&:hover': {
                              borderColor: '#e25822',
                            },
                            backgroundColor: alpha('#e25822', 0.04),
                            transition: 'border-color 0.2s ease',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <CardContent sx={{ p: 3, flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: '#1a1f36',
                                    mb: 1,
                                  }}
                                >
                                  {event.title}
                                </Typography>
                                <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#4f566b' }}>
                                    <CalendarTodayOutlinedIcon sx={{ fontSize: '1rem', mr: 1 }} />
                                    <Typography variant="body2">
                                      {format(parseISO(event.date), 'MMM d, yyyy')}
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#4f566b' }}>
                                    <AccessTimeOutlinedIcon sx={{ fontSize: '1rem', mr: 1 }} />
                                    <Typography variant="body2">
                                      {event.startTime} - {event.endTime}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label="Blocked"
                                    size="small"
                                    sx={{
                                      backgroundColor: alpha('#e25822', 0.1),
                                      color: '#e25822',
                                      borderRadius: '6px',
                                      height: '24px',
                                      fontSize: '12px',
                                    }}
                                  />
                                </Stack>
                                {event.description && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: '#4f566b',
                                      fontSize: '14px',
                                      mb: 2,
                                    }}
                                  >
                                    {event.description}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {event.courtIds.map(courtId => {
                                    const court = courts.find(c => c.id === courtId);
                                    return court ? (
                                      <Chip
                                        key={courtId}
                                        label={court.name}
                                        size="small"
                                        sx={{
                                          backgroundColor: alpha('#e25822', 0.1),
                                          color: '#e25822',
                                          borderRadius: '6px',
                                          height: '24px',
                                          fontSize: '12px',
                                        }}
                                      />
                                    ) : null;
                                  })}
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  onClick={() => handleEditEvent(event)}
                                  sx={{
                                    color: '#4f566b',
                                    '&:hover': {
                                      backgroundColor: alpha('#e25822', 0.04),
                                      color: '#e25822',
                                    },
                                  }}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  onClick={() => event.id && handleDeleteEvent(event.id)}
                                  sx={{
                                    color: '#4f566b',
                                    '&:hover': {
                                      backgroundColor: alpha('#ff4d4f', 0.04),
                                      color: '#ff4d4f',
                                    },
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box 
                    sx={{ 
                      py: 6,
                      textAlign: 'center',
                      backgroundColor: '#fff',
                      borderRadius: 1,
                      border: '1px dashed',
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#4f566b',
                        fontSize: '14px',
                      }}
                    >
                      No blocked time
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 3 && (
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#1a1f36',
                    mb: 3,
                  }}
                >
                  Past Events
                </Typography>
                {pastEvents.length > 0 ? (
                  <Stack spacing={2}>
                    {pastEvents.map(event => (
                      <Card
                        key={event.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'rgba(0, 0, 0, 0.1)',
                          boxShadow: 'none',
                          opacity: 0.8,
                          '&:hover': {
                            borderColor: '#625afa',
                          },
                          transition: 'border-color 0.2s ease',
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#1a1f36',
                                  mb: 1,
                                }}
                              >
                                {event.title}
                              </Typography>
                              <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: '#4f566b' }}>
                                  <CalendarTodayOutlinedIcon sx={{ fontSize: '1rem', mr: 1 }} />
                                  <Typography variant="body2">
                                    {format(parseISO(event.date), 'MMM d, yyyy')}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: '#4f566b' }}>
                                  <AccessTimeOutlinedIcon sx={{ fontSize: '1rem', mr: 1 }} />
                                  <Typography variant="body2">
                                    {event.startTime} - {event.endTime}
                                  </Typography>
                                </Box>
                                <StatusChip status={"Completed"} />
                              </Stack>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                onClick={() => handleEditEvent(event)}
                                sx={{
                                  color: '#4f566b',
                                  '&:hover': {
                                    backgroundColor: alpha('#625afa', 0.04),
                                    color: '#625afa',
                                  },
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => event.id && handleDeleteEvent(event.id)}
                                sx={{
                                  color: '#4f566b',
                                  '&:hover': {
                                    backgroundColor: alpha('#ff4d4f', 0.04),
                                    color: '#ff4d4f',
                                  },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box 
                    sx={{ 
                      py: 6,
                      textAlign: 'center',
                      backgroundColor: '#fff',
                      borderRadius: 1,
                      border: '1px dashed',
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#4f566b',
                        fontSize: '14px',
                      }}
                    >
                      No past events
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        <EventModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveEvent}
          event={selectedEvent}
          mode={modalMode}
          courts={courts.sort((a, b) => a.name.localeCompare(b.name))}
          timeOptions={timeOptions}
          onDateChange={(date) => generateTimeOptions(date)}
          facilityId={facilityId}
          businessId={businessId}
          loading={isSaving}
        />

        {showRecurringEventModal && (
          <RecurringEventModal
            open={showRecurringEventModal}
            onClose={handleCloseRecurringEventModal}
            onSave={handleSaveRecurringEvent}
            facilityId={facilityId}
            businessId={businessId}
            courts={courts}
            mode={selectedRecurringEvent ? 'edit' : 'create'}
            event={selectedRecurringEvent}
            timeOptions={timeOptions}
          />
        )}

        {/* Players Modal */}
        <Dialog open={isPlayersModalOpen} onClose={handleClosePlayersModal} maxWidth="xs" fullWidth>
          <DialogTitle>
            {selectedEventForPlayers?.title || 'Event Participants'}
          </DialogTitle>
          <DialogContent>
            <List>
              {selectedEventForPlayers?.participants && selectedEventForPlayers.participants.length > 0 ? (
                selectedEventForPlayers.participants.map((participant) => (
                  <ListItem key={participant.userId}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#625afa' }}>
                        {participant.name
                          ? participant.name.split(' ').map(n => n[0]).join('').toUpperCase()
                          : participant.userId.slice(0, 2).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={participant.name || participant.userId}
                      secondary={participant.userData?.email || null}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="No participants yet." />
                </ListItem>
              )}
            </List>
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default BusinessEvents; 